/**
 * Who owes an agreement: everybody, one application's users, or one security
 * group's members (auth#612).
 *
 * ── Why this is one module rather than three ternaries ───────────────
 *
 * The scope badge used to be `appSlug ? appSlug : 'org-wide'`, written out on
 * the list, the detail screen and the account panel. When auth#613 added a
 * group audience, every copy went on calling the Testing Terms org-wide, which
 * is the opposite of what they are: owed by testers and nobody else. A group
 * agreement has a null `appSlug` too, so "no app" stopped meaning "everybody".
 * One function that every badge reads cannot drift that way again.
 *
 * The audience is fixed when the agreement is created: auth's PATCH takes
 * `active` and nothing else. A different audience is a different agreement.
 */

export type AudienceKind = 'org' | 'app' | 'group';

/** The two fields every agreement row carries. `groupSlug` is optional so a
 *  response from an auth that predates auth#613 still reads as before. */
export type HasAudience = { appSlug: string | null; groupSlug?: string | null };

export function audienceKind(a: HasAudience): AudienceKind {
  if (a.groupSlug) return 'group';
  if (a.appSlug) return 'app';
  return 'org';
}

/**
 * The badge text. An app keeps its bare slug, as it always has; a group is
 * prefixed, because `testers` alone could be read as an application.
 */
export function audienceLabel(a: HasAudience): string {
  if (a.groupSlug) return `group: ${a.groupSlug}`;
  if (a.appSlug) return a.appSlug;
  return 'org-wide';
}

/**
 * The audience half of the create body. Never both fields: auth refuses that
 * with SCOPE_CONFLICT, and a form that kept a stale app while the group was
 * chosen would send exactly that. A kind whose slug is still blank sends
 * nothing, so the caller's disabled button is what stops it, not an org-wide
 * agreement created by accident.
 */
export function audienceBody(
  kind: AudienceKind,
  slugs: { appSlug: string; groupSlug: string },
): { appSlug: string } | { groupSlug: string } | Record<string, never> | null {
  if (kind === 'org') return {};
  const slug = (kind === 'app' ? slugs.appSlug : slugs.groupSlug).trim();
  if (!slug) return null;
  return kind === 'app' ? { appSlug: slug } : { groupSlug: slug };
}

/**
 * Which create-form field a refusal from auth belongs under, by its `code`
 * (services/auth/src/plugins/agreements-admin.ts). Null leaves it to the
 * form-level note: a code this does not know is still shown, just not pinned.
 */
export function refusedField(code: string | undefined): 'slug' | 'audience' | null {
  switch (code) {
    case 'SLUG_TAKEN':
      return 'slug';
    case 'SCOPE_CONFLICT':
    case 'UNKNOWN_GROUP':
    case 'UNKNOWN_APPLICATION':
      return 'audience';
    default:
      return null;
  }
}
